import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { X, Search, ArrowLeft, TrendingUp, Clock, MapPin, Star, Tag, Briefcase, Sparkles, Grid3X3, ChevronRight } from 'lucide-react';
import { useSearchFeed, FeedEntry } from '../hooks/useFeedData';
import { useHistory } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { ITEM_SUBCATEGORIES } from '../../lib/constants';
import { supabase } from '../../lib/db';

interface SearchOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

// Category icons as emoji for compact display
const CATEGORY_ICONS: Record<string, string> = {
  'phone-tablets': '📱',
  'electronics': '🔌',
  'clothing': '👕',
  'health': '💊',
  'home-garden': '🏠',
  'sports': '⚽',
  'toys': '🧸',
  'books': '📚',
  'food': '🍲',
  'industrial-equipment': '🏗️',
  'vehicles and spairs': '🚗',
  'property': '🏢',
  'computers': '💻',
  'baby-kids': '👶',
  'jewelry-watches': '💍',
  'pet-supplies': '🐾',
  'agriculture': '🌾',
  'services': '🔧',
};

const TRENDING_TERMS = {
  en: ['Phones', 'Laptops', 'Hair Stylist', 'Furniture', 'Plumber', 'Cars', 'Clothing', 'Electrician'],
  am: ['ስልክ', 'ላፕቶፕ', 'ፀጉር', 'ዕቃ ቤት', 'ቧንቧ ሰራተኛ', 'መኪና', 'ልብስ', 'ኤሌክትሪሻን'],
};

const RECENT_SEARCHES_KEY = 'warka_recent_searches';
const MAX_RECENT = 8;

const getRecentSearches = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem(RECENT_SEARCHES_KEY) || '[]');
  } catch {
    return [];
  }
};

const addRecentSearch = (query: string) => {
  const recent = getRecentSearches().filter((s) => s !== query);
  recent.unshift(query);
  localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
};

const clearRecentSearches = () => {
  localStorage.removeItem(RECENT_SEARCHES_KEY);
};

const formatPrice = (price: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'ETB',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
};

export const SearchOverlay: React.FC<SearchOverlayProps> = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [showCategories, setShowCategories] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { results, loading, aiLoading, search, clear, lastSmartResult } = useSearchFeed();
  const history = useHistory();
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const lastSearchedQuery = useRef<string>('');
  const { t, language } = useLanguage();

  const categoryKeys = useMemo(() => Object.keys(ITEM_SUBCATEGORIES), []);

  // Featured item strips for idle state
  const [recentItems, setRecentItems] = useState<any[]>([]);
  const [popularItems, setPopularItems] = useState<any[]>([]);

  useEffect(() => {
    if (!isOpen) return;

    const fetchStrips = async () => {
      // Recently added items
      const { data: recent } = await supabase
        .from('items')
        .select('id, name, price, new_price, item_images(*), seller_profiles!inner(location, is_deactivated)')
        .eq('seller_profiles.is_deactivated', false)
        .eq('hide_listing', false)
        .order('created_at', { ascending: false })
        .limit(10);

      // Popular items (highest rated with most reviews)
      const { data: popular } = await supabase
        .from('items')
        .select('id, name, price, new_price, item_images(*), seller_profiles!inner(location, is_deactivated)')
        .eq('seller_profiles.is_deactivated', false)
        .eq('hide_listing', false)
        .gt('reviews_count', 0)
        .order('rating', { ascending: false })
        .order('reviews_count', { ascending: false })
        .limit(10);

      if (recent) setRecentItems(recent);
      if (popular && popular.length > 0) {
        setPopularItems(popular);
      } else if (recent) {
        // Shuffle recent as "popular" fallback
        setPopularItems([...recent].sort(() => Math.random() - 0.5));
      }
    };

    fetchStrips();
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setRecentSearches(getRecentSearches());
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      setQuery('');
      clear();
      setActiveCategory(null);
      setShowCategories(false);
      lastSearchedQuery.current = '';
    }
  }, [isOpen, clear]);

  // Debounced search with AI
  const handleQueryChange = useCallback(
    (value: string) => {
      setQuery(value);
      if (debounceTimer.current) clearTimeout(debounceTimer.current);

      const trimmed = value.trim();

      if (!trimmed) {
        lastSearchedQuery.current = '';
        if (activeCategory) {
          search('', 'all', activeCategory, language, false);
        } else {
          clear();
        }
        return;
      }

      // Skip re-search if trimmed query hasn't changed (e.g. added trailing space)
      if (trimmed === lastSearchedQuery.current) return;

      debounceTimer.current = setTimeout(() => {
        lastSearchedQuery.current = trimmed;
        search(value, 'all', activeCategory || undefined, language, true);
        addRecentSearch(trimmed);
        setRecentSearches(getRecentSearches());
      }, 500);
    },
    [search, clear, activeCategory, language]
  );

  const handleCategoryChange = useCallback(
    (categoryKey: string | null) => {
      setActiveCategory(categoryKey);
      setShowCategories(false);
      if (!categoryKey) {
        if (!query.trim()) {
          clear();
          return;
        }
        search(query, 'all', undefined, language, true);
        return;
      }
      search(query, 'all', categoryKey, language, !!query.trim());
    },
    [query, search, clear, language]
  );

  const handleRecentClick = (q: string) => {
    setQuery(q);
    handleQueryChange(q);
  };

  const handleResultClick = (entry: FeedEntry) => {
    onClose();
    if (entry.type === 'item') {
      history.push(`/itemDetails/${entry.id}`);
    } else {
      history.push(`/provider/${entry.data.user_id || entry.id}`);
    }
  };

  const handleClearRecent = () => {
    clearRecentSearches();
    setRecentSearches([]);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      addRecentSearch(query.trim());
      setRecentSearches(getRecentSearches());
    }
    search(query, 'all', activeCategory || undefined, language, true);
  };

  if (!isOpen) return null;

  return (
    <div className="search-overlay flex flex-col">
      {/* Search Header */}
      <div className="flex-shrink-0 safe-area-top">
        <form onSubmit={handleSubmit} className="flex items-center gap-3 p-4">
          <button
            type="button"
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-full active:scale-90 transition-transform"
          >
            <ArrowLeft className="w-5 h-5" style={{ color: 'var(--theme-text)' }} />
          </button>

          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--theme-text-muted)' }} />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => handleQueryChange(e.target.value)}
              placeholder={language === 'am' ? 'ፈልግ...' : 'Search items and services...'}
              className="w-full pl-10 pr-10 py-3 bg-white/10 border border-white/10 rounded-full placeholder-white/40 text-sm focus:outline-none focus:border-primary-500/50 focus:bg-white/15 transition-all"
              style={{ color: 'var(--theme-text)', '--placeholder-color': 'var(--theme-text-muted)' } as React.CSSProperties}
            />
            {query && (
              <button
                type="button"
                onClick={() => {
                  setQuery('');
                  if (activeCategory) {
                    search('', 'all', activeCategory, language, false);
                  } else {
                    clear();
                  }
                  inputRef.current?.focus();
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2"
              >
                <X className="w-4 h-4" style={{ color: 'var(--theme-text-muted)' }} />
              </button>
            )}
          </div>
        </form>

        {/* Category pills — horizontal scroller */}
        <div className="px-4 pb-3 overflow-x-auto scrollbar-none">
          <div className="flex gap-2">
            <button
              onClick={() => handleCategoryChange(null)}
              className={`flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 ${
                !activeCategory ? 'bg-primary-500 text-white' : ''
              }`}
              style={activeCategory ? { backgroundColor: 'var(--theme-bg-soft)', border: '1px solid var(--theme-border)', color: 'var(--theme-text-soft)' } : undefined}
            >
              {language === 'am' ? 'ሁሉም' : 'All'}
            </button>
            <button
              onClick={() => setShowCategories(!showCategories)}
              className="flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 flex items-center gap-1"
              style={{ backgroundColor: 'var(--theme-bg-soft)', border: '1px solid var(--theme-border)', color: 'var(--theme-text-soft)' }}
            >
              <Grid3X3 className="w-3 h-3" />
              {language === 'am' ? 'ምድቦች' : 'Browse'}
            </button>
            {categoryKeys.slice(0, 8).map((key) => {
              const cat = ITEM_SUBCATEGORIES[key];
              return (
                <button
                  key={key}
                  onClick={() => handleCategoryChange(key)}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 ${
                    activeCategory === key ? 'bg-primary-500 text-white' : ''
                  }`}
                  style={activeCategory !== key ? { backgroundColor: 'var(--theme-bg-soft)', border: '1px solid var(--theme-border)', color: 'var(--theme-text-soft)' } : undefined}
                >
                  {CATEGORY_ICONS[key] || '📦'} {language === 'am' ? cat.am : cat.en}
                </button>
              );
            })}
          </div>
        </div>

        <div className="h-px" style={{ backgroundColor: 'var(--theme-border)' }} />
      </div>

      {/* Search Results / Recent Searches / Category Grid */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {/* Full category grid */}
        {showCategories && (
          <div className="mb-6">
            <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--theme-text-soft)' }}>
              {language === 'am' ? 'ምድቦችን ዳስስ' : 'Browse Categories'}
            </h3>
            <div className="grid grid-cols-3 gap-2">
              {categoryKeys.map((key) => {
                const cat = ITEM_SUBCATEGORIES[key];
                return (
                  <button
                    key={key}
                    onClick={() => handleCategoryChange(key)}
                    className={`flex flex-col items-center gap-1 p-3 rounded-xl text-center transition-all ${
                      activeCategory === key ? 'ring-2 ring-primary-500' : ''
                    }`}
                    style={{ backgroundColor: 'var(--theme-bg-soft)', border: '1px solid var(--theme-border)' }}
                  >
                    <span className="text-2xl">{CATEGORY_ICONS[key] || '📦'}</span>
                    <span className="text-[10px] font-medium leading-tight line-clamp-2" style={{ color: 'var(--theme-text-soft)' }}>
                      {language === 'am' ? cat.am : cat.en}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* AI search indicator */}
        {aiLoading && (
          <div className="flex items-center gap-2 mb-3 px-2 py-2 rounded-lg" style={{ backgroundColor: 'var(--color-primary-600)', color: 'white' }}>
            <Sparkles className="w-4 h-4 animate-pulse" />
            <span className="text-xs font-medium">
              {language === 'am' ? 'AI ፍለጋ በሂደት ላይ...' : 'AI is searching...'}
            </span>
          </div>
        )}

        {/* AI context chip */}
        {!aiLoading && lastSmartResult && results.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 mb-3">
            <Sparkles className="w-3.5 h-3.5" style={{ color: 'var(--color-primary-600)' }} />
            <span className="text-[10px] font-medium" style={{ color: 'var(--theme-text-muted)' }}>
              {language === 'am' ? 'AI ፍለጋ' : 'AI enhanced'}
            </span>
            {lastSmartResult.category && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-primary-500/10" style={{ color: 'var(--color-primary-600)' }}>
                {CATEGORY_ICONS[lastSmartResult.category]} {ITEM_SUBCATEGORIES[lastSmartResult.category]?.[language === 'am' ? 'am' : 'en'] || lastSmartResult.category}
              </span>
            )}
            {lastSmartResult.condition && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-orange-500/10 text-orange-600">
                {lastSmartResult.condition === 'new' ? (language === 'am' ? 'አዲስ' : 'New') : (language === 'am' ? 'ያገለገለ' : 'Used')}
              </span>
            )}
          </div>
        )}

        {/* Loading state */}
        {loading && !aiLoading && (
          <div className="grid grid-cols-2 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="rounded-xl overflow-hidden">
                <div className="feed-shimmer aspect-[3/4] rounded-xl" />
                <div className="mt-2 space-y-1.5">
                  <div className="feed-shimmer h-3 w-3/4 rounded" />
                  <div className="feed-shimmer h-3 w-1/2 rounded" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Results */}
        {!loading && results.length > 0 && (
          <>
            <p className="text-xs font-medium mb-3" style={{ color: 'var(--theme-text-muted)' }}>
              {results.length} result{results.length > 1 ? 's' : ''} found
            </p>
            <div className="grid grid-cols-2 gap-3">
              {results.map((entry) => (
                <button
                  key={`${entry.type}-${entry.id}`}
                  onClick={() => handleResultClick(entry)}
                  className="search-result-card text-left" style={{ backgroundColor: 'var(--theme-bg-soft)', border: '1px solid var(--theme-border)' }}
                >
                  {/* Image */}
                  <div className="aspect-[3/4] relative overflow-hidden rounded-t-xl bg-white/5">
                    {entry.type === 'item' && entry.data.item_images?.[0]?.image_url ? (
                      <img
                        src={entry.data.item_images[0].image_url}
                        alt={entry.data.name}
                        className="w-full h-full object-cover"
                      />
                    ) : entry.type === 'provider' && entry.data.image_url ? (
                      <img
                        src={entry.data.image_url}
                        alt={entry.data.business_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center" style={{ background: 'linear-gradient(to bottom right, var(--theme-bg-soft), var(--theme-surface))' }}>
                        {entry.type === 'item' ? (
                          <Tag className="w-12 h-12" style={{ color: 'var(--theme-text-muted)' }} />
                        ) : (
                          <Briefcase className="w-12 h-12" style={{ color: 'var(--theme-text-muted)' }} />
                        )}
                      </div>
                    )}
                    {/* Type badge */}
                    <div
                      className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        entry.type === 'item'
                          ? 'bg-orange-500/90 text-white'
                          : 'bg-primary-500/90 text-white'
                      }`}
                    >
                      {entry.type === 'item' ? 'Item' : 'Service'}
                    </div>
                    {/* Price overlay for items */}
                    {entry.type === 'item' && entry.data.price && (
                      <div className="absolute bottom-2 left-2 price-tag text-xs py-1 px-2">
                        {formatPrice(entry.data.new_price || entry.data.price)}
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-2.5">
                    <h3 className="text-sm font-semibold line-clamp-1" style={{ color: 'var(--theme-text)' }}>
                      {entry.type === 'item'
                        ? entry.data.name
                        : entry.data.business_name || 'Service Provider'}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      {entry.type === 'provider' && entry.data.rating > 0 && (
                        <span className="flex items-center gap-0.5 text-xs" style={{ color: 'var(--theme-text-muted)' }}>
                          <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                          {entry.data.rating.toFixed(1)}
                        </span>
                      )}
                      {(entry.type === 'item'
                        ? entry.data.seller_profiles?.location
                        : entry.data.location) && (
                        <span className="flex items-center gap-0.5 text-xs truncate" style={{ color: 'var(--theme-text-muted)' }}>
                          <MapPin className="w-3 h-3 flex-shrink-0" />
                          {entry.type === 'item'
                            ? entry.data.seller_profiles?.location
                            : entry.data.location}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}

        {/* Empty state after search */}
        {!loading && !aiLoading && (query.trim() || activeCategory) && results.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Search className="w-16 h-16 mb-4" style={{ color: 'var(--theme-border)' }} />
            <p className="text-base font-medium mb-1" style={{ color: 'var(--theme-text-soft)' }}>
              {language === 'am' ? 'ውጤት አልተገኘም' : 'No results found'}
            </p>
            <p className="text-sm" style={{ color: 'var(--theme-text-muted)' }}>
              {language === 'am' ? 'ሌላ ቃል ይሞክሩ' : 'Try different keywords or categories'}
            </p>
          </div>
        )}

        {/* Recent searches (when no query and no category filter active) */}
        {!query.trim() && !activeCategory && !loading && !showCategories && (
          <>
            {recentSearches.length > 0 && (
              <div className="mb-8">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--theme-text-soft)' }}>
                    {language === 'am' ? 'የቅርብ ጊዜ ፍለጋዎች' : 'Recent Searches'}
                  </h3>
                  <button
                    onClick={handleClearRecent}
                    className="text-primary-400 text-xs font-medium"
                  >
                    {language === 'am' ? 'ሁሉንም አጥፋ' : 'Clear all'}
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {recentSearches.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => handleRecentClick(q)}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-full text-sm transition-colors"
                      style={{ backgroundColor: 'var(--theme-bg-soft)', border: '1px solid var(--theme-border)', color: 'var(--theme-text-soft)' }}
                    >
                      <Clock className="w-3 h-3" style={{ color: 'var(--theme-text-muted)' }} />
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Trending / suggestions */}
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wider mb-3 flex items-center gap-1.5" style={{ color: 'var(--theme-text-soft)' }}>
                <TrendingUp className="w-3.5 h-3.5" />
                {language === 'am' ? 'ተወዳጅ' : 'Trending'}
              </h3>
              <div className="flex flex-wrap gap-2">
                {(language === 'am' ? TRENDING_TERMS.am : TRENDING_TERMS.en).map(
                  (term) => (
                    <button
                      key={term}
                      onClick={() => handleRecentClick(term)}
                      className="px-3 py-2 rounded-full text-sm transition-colors"
                      style={{ backgroundColor: 'var(--theme-bg-soft)', border: '1px solid var(--theme-border)', color: 'var(--theme-text-muted)' }}
                    >
                      {term}
                    </button>
                  )
                )}
              </div>
            </div>

            {/* Quick category grid on home */}
            <div className="mt-6">
              <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--theme-text-soft)' }}>
                {language === 'am' ? 'ምድቦች' : 'Categories'}
              </h3>
              <div className="grid grid-cols-4 gap-2">
                {categoryKeys.slice(0, 8).map((key) => {
                  const cat = ITEM_SUBCATEGORIES[key];
                  return (
                    <button
                      key={key}
                      onClick={() => handleCategoryChange(key)}
                      className="flex flex-col items-center gap-1 p-2.5 rounded-xl transition-all"
                      style={{ backgroundColor: 'var(--theme-bg-soft)', border: '1px solid var(--theme-border)' }}
                    >
                      <span className="text-xl">{CATEGORY_ICONS[key] || '📦'}</span>
                      <span className="text-[9px] font-medium leading-tight text-center line-clamp-1" style={{ color: 'var(--theme-text-soft)' }}>
                        {language === 'am' ? cat.am : cat.en}
                      </span>
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => setShowCategories(true)}
                className="w-full mt-2 py-2 text-xs font-medium rounded-lg transition-colors"
                style={{ color: 'var(--color-primary-600)' }}
              >
                {language === 'am' ? 'ሁሉንም ምድቦች ይመልከቱ →' : 'View all categories →'}
              </button>
            </div>

            {/* Recently Added Items */}
            {recentItems.length > 0 && (
              <div className="mt-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--theme-text-soft)' }}>
                    {language === 'am' ? 'አዲስ የተጨመሩ' : 'Recently Added'}
                  </h3>
                  <button
                    onClick={() => { onClose(); history.push('/marketplace'); }}
                    className="flex items-center gap-0.5 text-xs font-medium"
                    style={{ color: 'var(--color-primary-600)' }}
                  >
                    {language === 'am' ? 'ሁሉንም' : 'See all'}
                    <ChevronRight className="w-3 h-3" />
                  </button>
                </div>
                <div className="flex gap-3 overflow-x-auto scrollbar-none pb-2">
                  {recentItems.map((item) => {
                    const img = item.item_images?.find((i: any) => i.is_primary)?.image_url || item.item_images?.[0]?.image_url;
                    return (
                      <button
                        key={item.id}
                        onClick={() => { onClose(); history.push(`/itemDetails/${item.id}`); }}
                        className="flex-shrink-0 w-28 text-left"
                      >
                        <div className="w-28 h-28 rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--theme-bg-soft)', border: '1px solid var(--theme-border)' }}>
                          {img ? (
                            <img src={img} alt={item.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Tag className="w-8 h-8" style={{ color: 'var(--theme-text-muted)' }} />
                            </div>
                          )}
                        </div>
                        <p className="mt-1.5 text-xs font-medium line-clamp-1" style={{ color: 'var(--theme-text)' }}>{item.name}</p>
                        <p className="text-xs font-semibold" style={{ color: 'var(--color-primary-600)' }}>
                          {formatPrice(item.new_price || item.price)}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Popular Items */}
            {popularItems.length > 0 && (
              <div className="mt-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--theme-text-soft)' }}>
                    {language === 'am' ? 'ተወዳጅ ዕቃዎች' : 'Popular Items'}
                  </h3>
                  <button
                    onClick={() => { onClose(); history.push('/marketplace'); }}
                    className="flex items-center gap-0.5 text-xs font-medium"
                    style={{ color: 'var(--color-primary-600)' }}
                  >
                    {language === 'am' ? 'ሁሉንም' : 'See all'}
                    <ChevronRight className="w-3 h-3" />
                  </button>
                </div>
                <div className="flex gap-3 overflow-x-auto scrollbar-none pb-2">
                  {popularItems.map((item) => {
                    const img = item.item_images?.find((i: any) => i.is_primary)?.image_url || item.item_images?.[0]?.image_url;
                    return (
                      <button
                        key={item.id}
                        onClick={() => { onClose(); history.push(`/itemDetails/${item.id}`); }}
                        className="flex-shrink-0 w-28 text-left"
                      >
                        <div className="w-28 h-28 rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--theme-bg-soft)', border: '1px solid var(--theme-border)' }}>
                          {img ? (
                            <img src={img} alt={item.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Tag className="w-8 h-8" style={{ color: 'var(--theme-text-muted)' }} />
                            </div>
                          )}
                        </div>
                        <p className="mt-1.5 text-xs font-medium line-clamp-1" style={{ color: 'var(--theme-text)' }}>{item.name}</p>
                        <p className="text-xs font-semibold" style={{ color: 'var(--color-primary-600)' }}>
                          {formatPrice(item.new_price || item.price)}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
